import { ShowBase, useShowContext, useTranslate } from "ra-core";
import { Link } from "react-router";
import { EditButton } from "@/components/admin/edit-button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

import type { ChampClass } from "../types";
import {
  ROSTER_STAGES,
  formatAgeRange,
  formatPrice,
  formatTimeRange,
} from "./classUtils";
import { AddPlayerToClassButton } from "./AddPlayerToClassButton";
import { RosterExportButton } from "./RosterExportButton";
import { RosterSection } from "./ClassRoster";

export const ClassShow = () => (
  <ShowBase>
    <ClassShowContent />
  </ShowBase>
);

const Stat = ({
  label,
  value,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  tone?: "warning";
}) => (
  <div className="flex flex-col rounded-md border px-4 py-2 min-w-28">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span
      className={`text-2xl font-semibold ${tone === "warning" ? "text-destructive" : ""}`}
    >
      {value}
    </span>
  </div>
);

const ClassShowContent = () => {
  const translate = useTranslate();
  const { record, isPending } = useShowContext<ChampClass>();
  if (isPending || !record) return null;

  const enrolled = record.nb_enrolled ?? 0;
  const day = record.day_of_week
    ? translate(`resources.classes.days.${record.day_of_week}`)
    : null;
  const details = [
    day,
    formatTimeRange(record.start_time, record.end_time),
    record.location,
    record.age_min != null || record.age_max != null
      ? `${translate("resources.classes.fields.ages")} ${formatAgeRange(record.age_min, record.age_max)}`
      : null,
    record.gender
      ? translate(`resources.classes.genders.${record.gender}`)
      : null,
    formatPrice(record.price_cents),
  ].filter(Boolean);

  return (
    <div className="mt-2 flex flex-col gap-4 pb-2">
      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex-1 min-w-60">
              <Link
                to={`/programs/${record.program_id}/show`}
                className="text-sm text-muted-foreground hover:underline"
              >
                {record.program_name}
              </Link>
              <h2 className="text-2xl font-semibold flex items-center gap-2">
                {record.name}
                {!record.active && (
                  <Badge variant="outline">
                    {translate("resources.classes.inactive")}
                  </Badge>
                )}
              </h2>
              <p className="text-sm text-muted-foreground">
                {details.join(" · ")}
              </p>
              {record.coach_name && (
                <p className="text-sm">
                  {translate("resources.classes.fields.coach_name")}:{" "}
                  {record.coach_name}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <AddPlayerToClassButton />
              <RosterExportButton />
              <EditButton />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Stat
              label={translate("resources.classes.roster.enrolled")}
              value={
                record.capacity != null
                  ? `${enrolled} / ${record.capacity}`
                  : enrolled
              }
            />
            <Stat
              label={translate("resources.classes.roster.spots_left")}
              value={
                record.spots_left == null
                  ? translate("resources.classes.roster.no_cap")
                  : record.spots_left === 0
                    ? translate("resources.classes.roster.full")
                    : record.spots_left
              }
              tone={record.spots_left === 0 ? "warning" : undefined}
            />
            <Stat
              label={translate("resources.classes.roster.waitlist")}
              value={record.nb_waitlist ?? 0}
            />
            <Stat
              label={translate("resources.classes.roster.unpaid")}
              value={record.nb_unpaid ?? 0}
              tone={record.nb_unpaid ? "warning" : undefined}
            />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="flex flex-col gap-6">
          <h2 className="text-xl font-semibold">
            {translate("resources.classes.roster.title")}
          </h2>
          {ROSTER_STAGES.map((stage) => (
            <RosterSection key={stage} stage={stage} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
};
